/**
 * MindCare Provider Component
 * P3 - Maintenance Engine Context Provider
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { mindCareEngine } from '../../services/MindCareMaintenanceEngine';
import { rhieEventBus, RHIE_EVENTS } from '../../services/zihinKilavuzuEventBus';
import { useRHIE } from '../rhie';

const MindCareContext = createContext(null);

export function MindCareProvider({ children }) {
  const { token, user } = useAuth();
  const rhieContext = useRHIE();
  
  // State
  const [initialized, setInitialized] = useState(false);
  const [streakShield, setStreakShield] = useState(null);
  const [bufferDays, setBufferDays] = useState(null);
  const [graceMode, setGraceMode] = useState(null);
  const [weeklyRhythm, setWeeklyRhythm] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [motivation, setMotivation] = useState(null);
  const [newUser, setNewUser] = useState(null);
  
  // Initialize engine when auth changes
  useEffect(() => {
    if (token && user && rhieContext?.engine) {
      mindCareEngine.initialize(token, user, rhieContext.engine).then(() => {
        setInitialized(true);
        updateStateFromEngine();
      });
    }
  }, [token, user, rhieContext?.engine]);
  
  // Update local state from engine
  const updateStateFromEngine = useCallback(() => {
    setStreakShield(mindCareEngine.getStreakShield());
    setBufferDays(mindCareEngine.getBufferDays());
    setGraceMode(mindCareEngine.getGraceMode());
    setWeeklyRhythm(mindCareEngine.getWeeklyRhythm());
    setRecovery(mindCareEngine.getRecoveryState());
    setMotivation(mindCareEngine.getMotivation());
    setNewUser(mindCareEngine.getNewUserState());
  }, []);
  
  // Listen for events
  useEffect(() => {
    const unsubscribes = [];
    
    // Ritual completed - update weekly rhythm
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.RITUAL_COMPLETED, () => {
        mindCareEngine.recordDailyCompletion();
        mindCareEngine.checkNewUserProgress();
        updateStateFromEngine();
      })
    );
    
    // Shield used
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.STREAK_SHIELD_USED, () => {
        updateStateFromEngine();
      })
    );
    
    // Buffer used
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.BUFFER_DAY_USED, () => {
        updateStateFromEngine();
      })
    );
    
    // Grace mode changes
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.GRACE_MODE_ACTIVATED, () => {
        updateStateFromEngine();
      })
    );
    
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.GRACE_MODE_ENDED, () => {
        updateStateFromEngine();
      })
    );
    
    // Recovery events
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.RECOVERY_RITUAL_COMPLETE, () => {
        updateStateFromEngine();
      })
    );
    
    // Weekly report
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.WEEKLY_REPORT_READY, () => {
        updateStateFromEngine();
      })
    );
    
    // Motivation changes
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.MOTIVATION_RESTORED, () => {
        updateStateFromEngine();
      })
    );
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [updateStateFromEngine]);
  
  // Actions
  const activateShield = useCallback(() => {
    const result = mindCareEngine.activateStreakShield();
    updateStateFromEngine();
    return result;
  }, [updateStateFromEngine]);
  
  const useBuffer = useCallback(() => {
    const result = mindCareEngine.useBufferDay();
    updateStateFromEngine();
    return result;
  }, [updateStateFromEngine]);
  
  const activateGrace = useCallback((days, reason) => {
    const result = mindCareEngine.activateGraceMode(days, reason);
    updateStateFromEngine();
    return result;
  }, [updateStateFromEngine]);
  
  const endGrace = useCallback(() => {
    const result = mindCareEngine.endGraceMode();
    updateStateFromEngine();
    return result;
  }, [updateStateFromEngine]);
  
  const completeRecovery = useCallback((session) => {
    const result = mindCareEngine.completeRecoveryRitual(session);
    updateStateFromEngine();
    return result;
  }, [updateStateFromEngine]);
  
  const boostMotivation = useCallback((reason) => {
    mindCareEngine.boostMotivation(reason);
    updateStateFromEngine();
  }, [updateStateFromEngine]);
  
  const runMaintenance = useCallback(async () => {
    await mindCareEngine.runDailyMaintenance();
    updateStateFromEngine();
  }, [updateStateFromEngine]);
  
  const value = {
    // State
    initialized,
    streakShield,
    bufferDays,
    graceMode,
    weeklyRhythm,
    recovery,
    motivation,
    newUser,
    
    // Derived state
    isProtected: mindCareEngine.isStreakProtected(),
    isInGraceMode: graceMode?.isActive || false,
    isInRecoveryMode: recovery?.isInRecoveryMode || false,
    motivationScore: motivation?.score || 0,
    weeklyConsistency: weeklyRhythm?.consistency || 0,
    
    // Actions
    activateShield,
    useBuffer,
    activateGrace,
    endGrace,
    completeRecovery,
    boostMotivation,
    runMaintenance,
    
    // Engine reference
    engine: mindCareEngine
  };
  
  return (
    <MindCareContext.Provider value={value}>
      {children}
    </MindCareContext.Provider>
  );
}

export function useMindCare() {
  const context = useContext(MindCareContext);
  if (!context) {
    // Return safe defaults when outside provider
    return {
      initialized: false,
      streakShield: null,
      bufferDays: null,
      graceMode: null,
      weeklyRhythm: null,
      recovery: null,
      motivation: null,
      newUser: null,
      isProtected: false,
      isInGraceMode: false,
      isInRecoveryMode: false,
      motivationScore: 0,
      weeklyConsistency: 0,
      activateShield: () => ({ success: false }),
      useBuffer: () => ({ success: false }),
      activateGrace: () => ({ success: false }),
      endGrace: () => ({ success: false }),
      completeRecovery: () => ({ success: false }),
      boostMotivation: () => {},
      runMaintenance: async () => {},
      engine: null
    };
  }
  return context;
}

export default MindCareProvider;
